

import React, { memo } from 'react';
import { Text, Container, Row, Col } from '../../../../ui-kit';
import { OutputAddonsList } from '../../../AddonsList';
import VideoExtrasList from '../../../VideoExtrasList';

import OutputsCreditDetails from './components/OutputsCreditDetails';
import ProjectCostSummary from '../../../ProjectCostSummary';
import getCreditsLabel from '../../../ProjectCostSummary/utils/getCreditsLabel';
import { resolveOutputCredits } from '../../utils';

import '../../styles/_payment-summary-content.scss';

// The modal body now renders one read-only cost breakdown for everyone — the
// in-modal credit editing that used to show for internal staff has moved to the
// dedicated Costs tab on the traffic page. Internal users (derived from
// `hideCreditsModification`) instead get an "Edit Costs" link in the summary
// panel that opens that tab; external users see no such control. All per-output
// and project totals are resolved through the read-only path so both audiences
// see an identical summary.
const PaymentSummaryContentV2 = ({
  videoId,
  getVideoCreditsCostValue,
  purchaseOrderId,
  ghost,
  outputValues,
  initialOpenIndex,
  hideCreditsModification,
  totalCost,
  appliedStockAssets,
  outputCreditAmounts,
  videoReadableId,
  summaryCreativeServicesCredits,
  financePerOutputCredits,
  onPurchaseIdUpdate,
  getFormattedTotalCost,
  handlePurchaseOrderIdChange,
  hasShutterStockAccess,
  totalProjectCost,
  handlePrint,
  canProcessPayment,
  hasPaid = false,
  amountPending = 0
}) => {
  // Gate on an explicit `false` (not just falsy): the prop is optional, so an
  // omitted value must default to hiding the internal-only "Edit Costs" link
  // rather than showing it. Internal surfaces pass `hideCreditsModification={false}`.
  const isInternalUser = hideCreditsModification === false;

  const totalConsumedCredits = `${getVideoCreditsCostValue} ${getCreditsLabel(getVideoCreditsCostValue)}`;
  const estimatedTotalCost = getFormattedTotalCost(totalCost, true);

  return (
    <div className="payment-summary-content">
      <Container>
        <Row justify="between">
          <Col xs={12} sm={12} md={7} lg={7}>
            <div className="payment-summary-content__container--outputs">
              {outputValues.map((outputValue) => {
                const outputId = outputValue.output.outputId;
                const {
                  productionCredits: outputProductionCredits,
                  creativeServicesCredits: outputCreativeServicesCredits,
                  totalCreditsWithCreativeServices
                } = resolveOutputCredits({
                  outputId,
                  isMaster: outputValue.output.isMaster,
                  outputCreditAmounts,
                  financePerOutputCredits,
                  hideCreditsModification: true,
                  summaryCreativeServicesCredits
                });
                const formattedTotalCostPerOutput = totalCreditsWithCreativeServices ?
                `${totalCreditsWithCreativeServices} ${getCreditsLabel(
                  totalCreditsWithCreativeServices
                )} + ${getFormattedTotalCost(outputValue.totalCost)}` :
                `${getFormattedTotalCost(outputValue.totalCost)}`;

                return (
                  <OutputsCreditDetails
                    key={outputValue.output.readableId}
                    title={outputValue.outputName}
                    initialOpenIndex={initialOpenIndex}
                    footerTotalValue={formattedTotalCostPerOutput}
                    footerStartAdornment={`${outputValue.numberOfAddons} Add-ons`}
                    isTitleString
                    isExpandable
                    isOpenedByDefault={false}
                    hasBottomDivider={outputValue.numberOfAddons > 0}
                    ghost={ghost}
                    hideCreditsModification
                    addedCreditPerOutput={outputProductionCredits}
                    creativeServicesCredits={outputCreativeServicesCredits}>

                    <OutputAddonsList
                      videoOutput={outputValue.output}
                      additionalOutputs={outputValue.additionalOutputs} />

                  </OutputsCreditDetails>);

              })}

              {!!appliedStockAssets?.length &&
              <div className="applied-extras">
                  <Text heading5>STOCK UNLIMITED</Text>
                  <div className="extras-list">
                    <VideoExtrasList
                    listExtras={appliedStockAssets}
                    hasShutterStockAccess={hasShutterStockAccess} />

                  </div>
                </div>}

            </div>
            <div className="payment-section__footer-note">
              <Text
                prefixDagger
                className="payment-section__footer-start-adornment-text"
                noMargin>

                Until completion, this charge may change depending on the type
                of service requested.
              </Text>
              <Text
                prefixDoubleDagger
                className="payment-section__footer-start-adornment-text"
                noMargin>

                This is an estimated total. A final project cost can not be
                provided until all iterations have been edited and project is
                complete.
              </Text>
            </div>
          </Col>
          <Col xs={12} sm={12} md={5} lg={5}>
            <ProjectCostSummary
              videoReadableId={videoReadableId}
              productionCredits={totalConsumedCredits}
              addonsExtrasCost={estimatedTotalCost}
              creativeServicesCredits={`${summaryCreativeServicesCredits} ${getCreditsLabel(summaryCreativeServicesCredits)}`}
              subtotal={totalProjectCost}
              onDownload={handlePrint}
              onEditCosts={
              isInternalUser ?
              () => window.open(`/traffic/video/${videoId}/#costs`, '_blank') :
              void 0}

              billing={{
                purchaseOrderId,
                onPurchaseOrderChange: handlePurchaseOrderIdChange,
                onPurchaseOrderUpdate: onPurchaseIdUpdate,
                canUpdatePurchaseOrder: canProcessPayment || hasPaid
              }}
              payment={{
                hasPaid,
                total: estimatedTotalCost,
                amountPaid: `(${estimatedTotalCost})`,
                amountDue: getFormattedTotalCost(amountPending, false),
                canProcessPayment
              }} />
          </Col>
        </Row>
      </Container>
    </div>);

};

export default memo(PaymentSummaryContentV2);
